import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { useAuth } from '../../context/AuthContext';
import {
  Building2,
  Shield,
  Box,
  Users,
  Key,
  Settings,
  Save,
  AlertCircle,
  CheckCircle,
  ArrowLeft,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MODULES = {
  eam: { name: 'EAM / CMMS', description: 'Asset management and maintenance', restricted: false },
  spare_parts: { name: 'Spare Parts & Inventory', description: 'Manage inventory and restock logic', restricted: false },
  site_ops: { name: 'Site Operations', description: 'Site visits and inspections', restricted: false },
  action_plans: { name: 'Action Plans', description: 'Action tracking and accountability', restricted: false },
  analytics: { name: 'Analytics', description: 'Reports and dashboards', restricted: false },
  commercial: { name: 'Commercial & Revenue', description: 'Contracts and revenue tracking', restricted: true },
  finance: { name: 'Finance & Governance', description: 'Invoicing and compliance', restricted: true },
};

export default function OrganizationSettingsPage() {
  const { id: orgId } = useParams();
  const navigate = useNavigate();
  const { user, org: currentOrg, hasRole, hasPermission } = useAuth();
  
  // If no orgId provided, use current user's org
  const targetOrgId = orgId || currentOrg?.id;
  
  const [organization, setOrganization] = useState(null);
  const [ssoConfig, setSsoConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  // Form states
  const [basicInfo, setBasicInfo] = useState({ name: '', domain: '', user_limit: 100 });
  const [enabledModules, setEnabledModules] = useState([]);
  const [azureSso, setAzureSso] = useState({
    enabled: false,
    client_id: '',
    client_secret: '',
    tenant_id: '',
    allowed_domains: '',
    auto_provision: false,
    default_role: 'org_user',
    require_admin_approval: false,
  });
  const [googleSso, setGoogleSso] = useState({
    enabled: false,
    client_id: '',
    client_secret: '',
    allowed_domains: '',
    auto_provision: false,
    default_role: 'org_user',
    require_admin_approval: false,
  });

  const fetchOrgData = useCallback(async () => {
    try {
      const [orgRes, ssoRes] = await Promise.all([
        axios.get(`${API}/organizations/${targetOrgId}`),
        axios.get(`${API}/organizations/${targetOrgId}/sso`),
      ]);
      
      const org = orgRes.data;
      setOrganization(org);
      setBasicInfo({ name: org.name, domain: org.domain, user_limit: org.user_limit || 100 });
      setEnabledModules(org.enabled_modules || []);
      
      const sso = ssoRes.data.sso_config || {};
      if (sso.azure_ad) {
        setAzureSso(prev => ({
          ...prev,
          ...sso.azure_ad,
          allowed_domains: (sso.azure_ad.allowed_domains || []).join(', '),
        }));
      }
      if (sso.google) {
        setGoogleSso(prev => ({
          ...prev,
          ...sso.google,
          allowed_domains: (sso.google.allowed_domains || []).join(', '),
        }));
      }
      setSsoConfig(ssoRes.data);
    } catch (error) {
      console.error('Failed to fetch organization:', error);
      setMessage({ type: 'error', text: 'Failed to load organization data' });
    } finally {
      setLoading(false);
    }
  }, [targetOrgId]);

  useEffect(() => {
    if (targetOrgId) {
      fetchOrgData();
    } else {
      setLoading(false);
    }
  }, [targetOrgId, fetchOrgData]);

  const handleSaveBasicInfo = async () => {
    setSaving(true);
    setMessage({ type: '', text: '' });
    try {
      await axios.patch(`${API}/organizations/${targetOrgId}`, basicInfo);
      setMessage({ type: 'success', text: 'Organization updated successfully' });
      fetchOrgData();
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Failed to update organization' });
    } finally {
      setSaving(false);
    }
  };

  const handleToggleModule = async (module, enabled) => {
    setSaving(true);
    try {
      if (enabled) {
        await axios.post(`${API}/organizations/${targetOrgId}/modules/${module}/enable`);
      } else {
        await axios.post(`${API}/organizations/${targetOrgId}/modules/${module}/disable`);
      }
      setEnabledModules((prev) => enabled ? [...prev, module] : prev.filter((m) => m !== module));
      setMessage({ type: 'success', text: `Module ${enabled ? 'enabled' : 'disabled'} successfully` });
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Failed to toggle module' });
    } finally {
      setSaving(false);
    }
  };

  const handleSaveSsoConfig = async (provider) => {
    setSaving(true);
    setMessage({ type: '', text: '' });
    try {
      const config = provider === 'azure_ad' ? azureSso : googleSso;
      const payload = {
        ...config,
        allowed_domains: config.allowed_domains.split(',').map((d) => d.trim()).filter(Boolean),
      };
      await axios.post(`${API}/organizations/${targetOrgId}/sso/${provider}`, payload);
      setMessage({ type: 'success', text: `${provider === 'azure_ad' ? 'Azure AD' : 'Google'} SSO configuration saved` });
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Failed to save SSO configuration' });
    } finally {
      setSaving(false);
    }
  };

  const handleTestSso = async (provider) => {
    try {
      const response = await axios.post(`${API}/organizations/${targetOrgId}/sso/${provider}/test`);
      setMessage({ type: response.data.status === 'success' ? 'success' : 'error', text: response.data.message });
    } catch (error) {
      setMessage({ type: 'error', text: 'SSO test failed' });
    }
  };

  const canManageOrg = hasRole(['superadmin', 'platform_admin']) || hasPermission('org.manage');
  const canConfigureSso = hasRole(['superadmin', 'platform_admin']) || hasPermission('org.sso_config');
  const canEnableRestrictedModules = hasRole(['superadmin']);

  if (!targetOrgId) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <Building2 className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-slate-900 mb-2">No Organization</h2>
            <p className="text-slate-500">Your account is not associated with an organization.</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin w-8 h-8 border-4 border-sky-500 border-t-transparent rounded-full" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="org-settings-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            {orgId && hasRole(['superadmin', 'platform_admin']) && (
              <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
                <ArrowLeft className="w-4 h-4" />
              </Button>
            )}
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Organization Settings</h1>
              <p className="text-slate-500">{organization?.name}</p>
            </div>
          </div>
          <Badge variant={organization?.status === 'active' ? 'default' : 'secondary'}>
            {organization?.status}
          </Badge>
        </div>

        {/* Message */}
        {message.text && (
          <div className={`flex items-center gap-2 p-4 rounded-lg ${
            message.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
          }`}>
            {message.type === 'success' ? <CheckCircle className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
            {message.text}
          </div>
        )}

        <Tabs defaultValue="general">
          <TabsList>
            <TabsTrigger value="general" data-testid="tab-general">
              <Building2 className="w-4 h-4 mr-2" />
              General
            </TabsTrigger>
            <TabsTrigger value="modules" data-testid="tab-modules">
              <Box className="w-4 h-4 mr-2" />
              Modules
            </TabsTrigger>
            <TabsTrigger value="sso" data-testid="tab-sso">
              <Key className="w-4 h-4 mr-2" />
              SSO
            </TabsTrigger>
          </TabsList>

          {/* General Tab */}
          <TabsContent value="general" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle>Organization Details</CardTitle>
                <CardDescription>Basic organization information</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="org-name">Organization Name</Label>
                    <Input
                      id="org-name"
                      value={basicInfo.name}
                      onChange={(e) => setBasicInfo({ ...basicInfo, name: e.target.value })}
                      disabled={!canManageOrg}
                      data-testid="org-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="org-domain">Domain</Label>
                    <Input
                      id="org-domain"
                      value={basicInfo.domain}
                      onChange={(e) => setBasicInfo({ ...basicInfo, domain: e.target.value })}
                      disabled={!canManageOrg}
                      data-testid="org-domain-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="user-limit">User Limit</Label>
                    <Input
                      id="user-limit"
                      type="number"
                      value={basicInfo.user_limit}
                      onChange={(e) => setBasicInfo({ ...basicInfo, user_limit: parseInt(e.target.value) })}
                      disabled={!hasRole(['superadmin', 'platform_admin'])}
                      data-testid="user-limit-input"
                    />
                  </div>
                </div>
                {canManageOrg && (
                  <Button onClick={handleSaveBasicInfo} disabled={saving} data-testid="save-org-btn">
                    <Save className="w-4 h-4 mr-2" />
                    {saving ? 'Saving...' : 'Save Changes'}
                  </Button>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Modules Tab */}
          <TabsContent value="modules" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle>Enabled Modules</CardTitle>
                <CardDescription>Control which features are available to this organization</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {Object.entries(MODULES).map(([key, module]) => {
                    const isEnabled = enabledModules.includes(key);
                    const canToggle = !module.restricted || canEnableRestrictedModules;
                    
                    return (
                      <div
                        key={key}
                        className={`flex items-center justify-between p-4 rounded-lg border ${
                          isEnabled ? 'border-sky-200 bg-sky-50' : 'border-slate-200'
                        }`}
                        data-testid={`module-${key}`}
                      >
                        <div className="flex items-center gap-4">
                          <Box className={`w-5 h-5 ${isEnabled ? 'text-sky-600' : 'text-slate-400'}`} />
                          <div>
                            <div className="flex items-center gap-2">
                              <p className="font-medium text-slate-900">{module.name}</p>
                              {module.restricted && (
                                <Badge variant="outline" className="text-xs">
                                  Restricted
                                </Badge>
                              )}
                            </div>
                            <p className="text-sm text-slate-500">{module.description}</p>
                          </div>
                        </div>
                        <Switch
                          checked={isEnabled}
                          onCheckedChange={(checked) => handleToggleModule(key, checked)}
                          disabled={!canToggle || saving}
                          data-testid={`module-switch-${key}`}
                        />
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* SSO Tab */}
          <TabsContent value="sso" className="mt-6 space-y-6">
            {/* Azure AD */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Shield className="w-5 h-5 text-blue-600" />
                  Azure Active Directory
                </CardTitle>
                <CardDescription>Configure Azure AD SSO for enterprise authentication</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <Label htmlFor="azure-enabled">Enable Azure AD SSO</Label>
                  <Switch
                    id="azure-enabled"
                    checked={azureSso.enabled}
                    onCheckedChange={(checked) => setAzureSso({ ...azureSso, enabled: checked })}
                    disabled={!canConfigureSso}
                    data-testid="azure-sso-enabled"
                  />
                </div>
                {azureSso.enabled && (
                  <div className="space-y-4 pt-4 border-t">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="azure-client-id">Client ID</Label>
                        <Input
                          id="azure-client-id"
                          value={azureSso.client_id}
                          onChange={(e) => setAzureSso({ ...azureSso, client_id: e.target.value })}
                          placeholder="Azure AD Application Client ID"
                          disabled={!canConfigureSso}
                          data-testid="azure-client-id"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="azure-tenant-id">Tenant ID</Label>
                        <Input
                          id="azure-tenant-id"
                          value={azureSso.tenant_id}
                          onChange={(e) => setAzureSso({ ...azureSso, tenant_id: e.target.value })}
                          placeholder="Azure AD Tenant ID"
                          disabled={!canConfigureSso}
                          data-testid="azure-tenant-id"
                        />
                      </div>
                      <div className="space-y-2 md:col-span-2">
                        <Label htmlFor="azure-secret">Client Secret</Label>
                        <Input
                          id="azure-secret"
                          type="password"
                          value={azureSso.client_secret}
                          onChange={(e) => setAzureSso({ ...azureSso, client_secret: e.target.value })}
                          placeholder="Azure AD Client Secret"
                          disabled={!canConfigureSso}
                          data-testid="azure-client-secret"
                        />
                      </div>
                      <div className="space-y-2 md:col-span-2">
                        <Label htmlFor="azure-domains">Allowed Domains (comma-separated)</Label>
                        <Input
                          id="azure-domains"
                          value={azureSso.allowed_domains}
                          onChange={(e) => setAzureSso({ ...azureSso, allowed_domains: e.target.value })}
                          placeholder="company.com, subsidiary.com"
                          disabled={!canConfigureSso}
                          data-testid="azure-allowed-domains"
                        />
                      </div>
                    </div>
                    <div className="flex items-center gap-6">
                      <div className="flex items-center gap-2">
                        <Switch
                          id="azure-auto-provision"
                          checked={azureSso.auto_provision}
                          onCheckedChange={(checked) => setAzureSso({ ...azureSso, auto_provision: checked })}
                          disabled={!canConfigureSso}
                        />
                        <Label htmlFor="azure-auto-provision">Auto-provision users</Label>
                      </div>
                      <div className="flex items-center gap-2">
                        <Switch
                          id="azure-require-approval"
                          checked={azureSso.require_admin_approval}
                          onCheckedChange={(checked) => setAzureSso({ ...azureSso, require_admin_approval: checked })}
                          disabled={!canConfigureSso}
                        />
                        <Label htmlFor="azure-require-approval">Require admin approval</Label>
                      </div>
                    </div>
                    {canConfigureSso && (
                      <div className="flex gap-2 pt-4">
                        <Button onClick={() => handleSaveSsoConfig('azure_ad')} disabled={saving} data-testid="save-azure-sso">
                          <Save className="w-4 h-4 mr-2" />
                          Save Configuration
                        </Button>
                        <Button variant="outline" onClick={() => handleTestSso('azure_ad')} data-testid="test-azure-sso">
                          Test Connection
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Google */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Shield className="w-5 h-5 text-red-500" />
                  Google Workspace
                </CardTitle>
                <CardDescription>Configure Google SSO for workspace authentication</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <Label htmlFor="google-enabled">Enable Google SSO</Label>
                  <Switch
                    id="google-enabled"
                    checked={googleSso.enabled}
                    onCheckedChange={(checked) => setGoogleSso({ ...googleSso, enabled: checked })}
                    disabled={!canConfigureSso}
                    data-testid="google-sso-enabled"
                  />
                </div>
                {googleSso.enabled && (
                  <div className="space-y-4 pt-4 border-t">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="google-client-id">Client ID</Label>
                        <Input
                          id="google-client-id"
                          value={googleSso.client_id}
                          onChange={(e) => setGoogleSso({ ...googleSso, client_id: e.target.value })}
                          placeholder="Google OAuth Client ID"
                          disabled={!canConfigureSso}
                          data-testid="google-client-id"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="google-secret">Client Secret</Label>
                        <Input
                          id="google-secret"
                          type="password"
                          value={googleSso.client_secret}
                          onChange={(e) => setGoogleSso({ ...googleSso, client_secret: e.target.value })}
                          placeholder="Google OAuth Client Secret"
                          disabled={!canConfigureSso}
                          data-testid="google-client-secret"
                        />
                      </div>
                      <div className="space-y-2 md:col-span-2">
                        <Label htmlFor="google-domains">Allowed Domains (comma-separated)</Label>
                        <Input
                          id="google-domains"
                          value={googleSso.allowed_domains}
                          onChange={(e) => setGoogleSso({ ...googleSso, allowed_domains: e.target.value })}
                          placeholder="company.com"
                          disabled={!canConfigureSso}
                          data-testid="google-allowed-domains"
                        />
                      </div>
                    </div>
                    <div className="flex items-center gap-6">
                      <div className="flex items-center gap-2">
                        <Switch
                          id="google-auto-provision"
                          checked={googleSso.auto_provision}
                          onCheckedChange={(checked) => setGoogleSso({ ...googleSso, auto_provision: checked })}
                          disabled={!canConfigureSso}
                        />
                        <Label htmlFor="google-auto-provision">Auto-provision users</Label>
                      </div>
                      <div className="flex items-center gap-2">
                        <Switch
                          id="google-require-approval"
                          checked={googleSso.require_admin_approval}
                          onCheckedChange={(checked) => setGoogleSso({ ...googleSso, require_admin_approval: checked })}
                          disabled={!canConfigureSso}
                        />
                        <Label htmlFor="google-require-approval">Require admin approval</Label>
                      </div>
                    </div>
                    {canConfigureSso && (
                      <div className="flex gap-2 pt-4">
                        <Button onClick={() => handleSaveSsoConfig('google')} disabled={saving} data-testid="save-google-sso">
                          <Save className="w-4 h-4 mr-2" />
                          Save Configuration
                        </Button>
                        <Button variant="outline" onClick={() => handleTestSso('google')} data-testid="test-google-sso">
                          Test Connection
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
}
